import React, { createContext, useContext, useMemo, useState } from "react";
import { AppLanguage, translations, TranslationKey } from "../i18n/translations";

const STORAGE_KEY = "documentation-language";

function readStoredLanguage(): AppLanguage {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (value === "fr" || value === "en") {
      return value;
    }
  } catch {}
  return "fr";
}

type I18nContextValue = {
  language: AppLanguage;
  setLanguage: (language: AppLanguage) => void;
  t: (key: TranslationKey, vars?: Record<string, string>) => string;
  levelLabel: (level: string) => string;
};

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<AppLanguage>(() => readStoredLanguage());

  const setLanguage = (nextLanguage: AppLanguage) => {
    setLanguageState(nextLanguage);
    try {
      localStorage.setItem(STORAGE_KEY, nextLanguage);
    } catch {}
  };

  const t = (key: TranslationKey, vars?: Record<string, string>) => {
    const template = translations[language][key] ?? translations.fr[key] ?? key;
    if (!vars) {
      return template;
    }
    return template.replace(/\{(\w+)\}/g, (_, varName: string) => vars[varName] ?? "");
  };

  const levelLabel = (level: string) => {
    const key = `level.${level}` as TranslationKey;
    const hasKey = Object.prototype.hasOwnProperty.call(translations.fr, key);
    return hasKey ? t(key) : level;
  };

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t,
      levelLabel,
    }),
    [language],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useI18n must be used within I18nProvider");
  }
  return ctx;
}
